// BETALUMINA_FLOTTA_RIFORNIMENTI_V2E_CALIBRATION_PROGRESS_20260902
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://interventi.synology.me:5050";

const VEHICLE_KEY = "fiorino";

const localDateTimeValue = () => {
  const now = new Date();
  const local = new Date(
    now.getTime() -
      now.getTimezoneOffset() * 60000
  );
  return local.toISOString().slice(0, 16);
};

const toUtcIso = (localValue) => {
  if (!localValue) return null;
  const date = new Date(localValue);
  return Number.isNaN(date.getTime())
    ? null
    : date.toISOString();
};

const emptyForm = () => ({
  rifornitoIl: localDateTimeValue(),
  litri: "",
  prezzoEuroLitro: "",
  importoEuro: "",
  pieno: false,
  odometroKm: "",
  odometroSource: null,
  note: "",
});

const jwtLike = (value) =>
  typeof value === "string" &&
  value.trim().split(".").length === 3;

const extractToken = (value) => {
  if (!value) return null;
  if (jwtLike(value)) return value.trim();

  try {
    const parsed = JSON.parse(value);
    for (const key of [
      "token",
      "jwt",
      "accessToken",
      "access_token",
    ]) {
      if (jwtLike(parsed?.[key])) {
        return parsed[key].trim();
      }
    }
  } catch (_) {
    // non JSON
  }

  return null;
};

const getAuthHeaders = () => {
  const stores = [
    window.localStorage,
    window.sessionStorage,
  ];
  const preferredKeys = [
    "token",
    "authToken",
    "jwt",
    "accessToken",
    "access_token",
  ];

  for (const store of stores) {
    for (const key of preferredKeys) {
      const token = extractToken(
        store.getItem(key)
      );
      if (token) {
        return {
          Authorization: `Bearer ${token}`,
        };
      }
    }

    for (
      let i = 0;
      i < store.length;
      i += 1
    ) {
      const key = store.key(i);
      const token = extractToken(
        key ? store.getItem(key) : null
      );
      if (token) {
        return {
          Authorization: `Bearer ${token}`,
        };
      }
    }
  }

  return {};
};

const numberOrNull = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const n = Number(
    String(value).replace(",", ".")
  );

  return Number.isFinite(n) ? n : null;
};

const euro = (value, digits = 2) => {
  const n = numberOrNull(value);
  return n === null
    ? "—"
    : `${n.toFixed(digits)} €`;
};

const formatDateTime = (value) => {
  if (!value) return "—";

  const normalized =
    String(value).includes("T")
      ? String(value)
      : String(value).replace(" ", "T");

  const date = new Date(normalized);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      });
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cfd6df",
  borderRadius: 8,
  padding: "9px 10px",
  fontSize: 14,
  background: "#fff",
};

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
  fontSize: 13,
  fontWeight: 600,
};

const buttonStyle = {
  border: 0,
  borderRadius: 8,
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 700,
};

const tileStyle = {
  padding: 12,
  borderRadius: 8,
  background: "#f5f7fa",
};

const StatusPill = ({ children }) => (
  <span
    style={{
      display: "inline-block",
      padding: "3px 8px",
      borderRadius: 999,
      background: "#e8eef7",
      fontSize: 12,
      fontWeight: 700,
    }}
  >
    {children}
  </span>
);

const FlottaRifornimenti = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] =
    useState(emptyForm);
  const [editingId, setEditingId] =
    useState(null);
  const [loading, setLoading] =
    useState(true);
  const [saving, setSaving] =
    useState(false);
  const [assistLoading, setAssistLoading] =
    useState(false);
  const [assist, setAssist] =
    useState(null);
  const [calibration, setCalibration] =
    useState(null);

  const [tripCosts, setTripCosts] =
    useState(null);
  const [
    tripCostsLoading,
    setTripCostsLoading,
  ] = useState(false);
  const [
    tripCostsSyncing,
    setTripCostsSyncing,
  ] = useState(false);

  const [
    showAllTripCosts,
    setShowAllTripCosts,
  ] = useState(false);

  const [error, setError] =
    useState("");
  const [message, setMessage] =
    useState("");

  const headers = () =>
    getAuthHeaders();

  const loadTripCosts =
    useCallback(async () => {
      setTripCostsLoading(true);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/flotta/costi-viaggi`,
          {
            params: {
              vehicle: VEHICLE_KEY,
              limit: 100,
            },
            headers: getAuthHeaders(),
          }
        );

        setTripCosts(
          response.data || null
        );
      } catch (err) {
        console.warn(
          "Costi viaggi non disponibili:",
          err.response?.data?.error ||
            err.message
        );
      } finally {
        setTripCostsLoading(false);
      }
    }, []);

  const syncTripCosts =
    useCallback(async () => {
      setTripCostsSyncing(true);

      try {
        await axios.post(
          `${API_BASE_URL}/api/flotta/costi-viaggi/sync`,
          {
            vehicle: VEHICLE_KEY,
            days: 7,
            limit: 100,
          },
          {
            headers: getAuthHeaders(),
          }
        );

        await loadTripCosts();
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.message ||
            "Sincronizzazione costi viaggi non riuscita."
        );
      } finally {
        setTripCostsSyncing(false);
      }
    }, [loadTripCosts]);

  const loadCore = useCallback(
    async () => {
      const [
        refuelsResponse,
        calibrationResponse,
      ] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/api/flotta/rifornimenti`,
          {
            params: {
              vehicle: VEHICLE_KEY,
              limit: 200,
            },
            headers: getAuthHeaders(),
          }
        ),
        axios.get(
          `${API_BASE_URL}/api/flotta/rifornimenti/consumi`,
          {
            params: {
              vehicle: VEHICLE_KEY,
            },
            headers: getAuthHeaders(),
          }
        ),
      ]);

      setItems(
        Array.isArray(
          refuelsResponse.data?.items
        )
          ? refuelsResponse.data.items
          : []
      );

      setCalibration(
        calibrationResponse.data
          ?.calibration || null
      );

      await loadTripCosts();
    },
    [loadTripCosts]
  );

  const loadAssist = useCallback(
    async (localValue) => {
      const atIso =
        toUtcIso(localValue);

      if (!atIso) return;

      setAssistLoading(true);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/flotta/rifornimenti/assist`,
          {
            params: {
              vehicle: VEHICLE_KEY,
              at: atIso,
            },
            headers: getAuthHeaders(),
          }
        );

        setAssist(
          response.data?.assist || null
        );
      } catch (err) {
        console.warn(
          "Assist telemetria non disponibile:",
          err.response?.data?.error ||
            err.message
        );
      } finally {
        setAssistLoading(false);
      }
    },
    []
  );

  const load = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        await loadCore();

        if (!editingId) {
          await loadAssist(
            form.rifornitoIl
          );
        }
      } catch (err) {
        setError(
          err.response?.status === 403
            ? "Accesso rifornimenti negato: sessione non valida."
            : err.response?.data?.error ||
                err.message ||
                "Impossibile caricare i rifornimenti."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      editingId,
      form.rifornitoIl,
      loadAssist,
      loadCore,
    ]
  );

  useEffect(() => {
    load();
    // Il primo caricamento è intenzionale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editingId) return undefined;

    const timer = window.setTimeout(
      () => {
        loadAssist(
          form.rifornitoIl
        );
      },
      700
    );

    return () =>
      window.clearTimeout(timer);
  }, [
    editingId,
    form.rifornitoIl,
    loadAssist,
  ]);

  const summary = useMemo(() => {
    const totalLitri = items.reduce(
      (sum, item) =>
        sum +
        (Number(item.litri) || 0),
      0
    );

    const totalEuro = items.reduce(
      (sum, item) =>
        sum +
        (Number(item.importoEuro) ||
          0),
      0
    );

    const pricedLiters =
      items.reduce(
        (sum, item) =>
          sum +
          (Number.isFinite(
            Number(
              item.prezzoEuroLitro
            )
          )
            ? Number(item.litri) || 0
            : 0),
        0
      );

    const weightedPrice =
      pricedLiters > 0
        ? items.reduce(
            (sum, item) =>
              sum +
              (Number(
                item.prezzoEuroLitro
              ) || 0) *
                (Number(item.litri) ||
                  0),
            0
          ) / pricedLiters
        : null;

    return {
      totalLitri,
      totalEuro,
      weightedPrice,
      pieni: items.filter(
        (item) => item.pieno
      ).length,
    };
  }, [items]);


  // BETALUMINA_REFUEL_DASHBOARD_V1_20260926
  const refuelDashboard = useMemo(() => {
    const chronological = [...items].sort((a, b) => {
      const ta = new Date(a.rifornitoIl || a.eventTimeUtc || 0).getTime();
      const tb = new Date(b.rifornitoIl || b.eventTimeUtc || 0).getTime();
      return ta - tb;
    });

    const latest = chronological.length
      ? chronological[chronological.length - 1]
      : null;

    const intervals = Array.isArray(calibration?.intervals)
      ? calibration.intervals
      : [];

    const completed = intervals.length
      ? intervals[intervals.length - 1]
      : null;

    const distanceKm = Number(completed?.distanceKm);
    const realLiters = Number(completed?.realLiters);
    const lPer100 = Number(completed?.realLPer100Km);

    const kmPerLiter =
      Number.isFinite(lPer100) && lPer100 > 0
        ? 100 / lPer100
        : null;

    const estimatedIntervalCost =
      Number.isFinite(realLiters) &&
      summary.weightedPrice !== null
        ? realLiters * summary.weightedPrice
        : null;

    const euroPerKm =
      Number.isFinite(distanceKm) &&
      distanceKm > 0 &&
      estimatedIntervalCost !== null
        ? estimatedIntervalCost / distanceKm
        : null;

    const current = calibration?.currentInterval || null;
    const currentDistance = Number(current?.distance?.distanceKm);
    const currentMaf = Number(current?.rawMaf?.airMassGrams);
    const currentCoverage = Number(current?.rawMaf?.coverageSeconds);

    const profileCoeff = Number(
      calibration?.profile?.empiricalAirGramsPerLiter
    );
    const completedMafGrams = Number(completed?.maf?.airMassGrams);

    const mafEstimatedLiters =
      Number.isFinite(profileCoeff) &&
      profileCoeff > 0 &&
      Number.isFinite(completedMafGrams)
        ? completedMafGrams / profileCoeff
        : null;

    const mafDeltaPct =
      mafEstimatedLiters !== null &&
      Number.isFinite(realLiters) &&
      realLiters > 0
        ? ((mafEstimatedLiters - realLiters) / realLiters) * 100
        : null;

    return {
      latest,
      completed,
      distanceKm: Number.isFinite(distanceKm) ? distanceKm : null,
      realLiters: Number.isFinite(realLiters) ? realLiters : null,
      lPer100: Number.isFinite(lPer100) ? lPer100 : null,
      kmPerLiter,
      estimatedIntervalCost,
      euroPerKm,
      current,
      currentDistance: Number.isFinite(currentDistance) ? currentDistance : null,
      currentMaf: Number.isFinite(currentMaf) ? currentMaf : null,
      currentCoverage: Number.isFinite(currentCoverage) ? currentCoverage : null,
      mafEstimatedLiters,
      mafDeltaPct,
    };
  }, [items, calibration, summary]);

  const updateField = (
    name,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const updateOdometer = (value) => {
    setForm((current) => ({
      ...current,
      odometroKm: value,
      odometroSource: "manuale_cruscotto",
    }));
  };

  const onLitriOrPrezzo = (
    name,
    value
  ) => {
    setForm((current) => {
      const next = {
        ...current,
        [name]: value,
      };

      const litri = numberOrNull(
        name === "litri"
          ? value
          : next.litri
      );

      const prezzo = numberOrNull(
        name === "prezzoEuroLitro"
          ? value
          : next.prezzoEuroLitro
      );

      if (
        litri !== null &&
        prezzo !== null &&
        !editingId
      ) {
        next.importoEuro = (
          litri * prezzo
        ).toFixed(2);
      }

      return next;
    });
  };

  const resetForm = () => {
    const next = emptyForm();
    setEditingId(null);
    setForm(next);
    setMessage("");
    setError("");
    loadAssist(
      next.rifornitoIl
    );
  };

  const editItem = (item) => {
    setEditingId(item.id);

    setForm({
      rifornitoIl: String(
        item.rifornitoIl || ""
      )
        .replace(" ", "T")
        .slice(0, 16),
      litri: item.litri ?? "",
      prezzoEuroLitro:
        item.prezzoEuroLitro ?? "",
      importoEuro:
        item.importoEuro ?? "",
      pieno: Boolean(item.pieno),
      odometroKm:
        item.odometroKm ?? "",
      odometroSource:
        item.odometroSource ||
        "manuale_cruscotto",
      note: item.note ?? "",
    });

    setMessage("");
    setError("");

    if (item.eventTimeUtc) {
      axios
        .get(
          `${API_BASE_URL}/api/flotta/rifornimenti/assist`,
          {
            params: {
              vehicle: VEHICLE_KEY,
              at: item.eventTimeUtc,
            },
            headers: headers(),
          }
        )
        .then((response) =>
          setAssist(
            response.data?.assist ||
              null
          )
        )
        .catch(() => {});
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const applyCandidate = (candidate) => {
    const date = new Date(
      candidate.timestamp
    );

    const local = new Date(
      date.getTime() -
        date.getTimezoneOffset() *
          60000
    )
      .toISOString()
      .slice(0, 16);

    setForm((current) => ({
      ...current,
      rifornitoIl: local,
    }));

    setMessage(
      `Candidato ${candidate.fuelBeforePct}% → ${candidate.fuelAfterPct}% agganciato. Inserisci litri reali e conferma se era pieno.`
    );
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    const eventTimeUtc =
      toUtcIso(form.rifornitoIl);

    const payload = {
      vehicle_key: VEHICLE_KEY,
      rifornito_il:
        form.rifornitoIl,
      event_time_utc:
        eventTimeUtc,
      litri: form.litri,
      prezzo_euro_litro:
        form.prezzoEuroLitro,
      importo_euro:
        form.importoEuro,
      pieno: form.pieno,
      odometro_km:
        form.odometroKm,
      odometro_source:
        form.odometroSource,
      note: form.note,
    };

    try {
      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/api/flotta/rifornimenti/${editingId}`,
          payload,
          {
            headers: headers(),
          }
        );

        setMessage(
          "Rifornimento aggiornato e telemetria riagganciata."
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/api/flotta/rifornimenti`,
          payload,
          {
            headers: headers(),
          }
        );

        setMessage(
          "Rifornimento registrato con snapshot telemetrico automatico."
        );
      }

      const next = emptyForm();
      setEditingId(null);
      setForm(next);

      await loadCore();
      await loadAssist(
        next.rifornitoIl
      );
    } catch (err) {
      setError(
        err.response?.data?.error ||
          (err.response?.status === 403
            ? "Accesso negato: sessione non valida."
            : err.message) ||
          "Salvataggio non riuscito."
      );
    } finally {
      setSaving(false);
    }
  };

  const candidate =
    assist?.candidates?.[0] ||
    null;

  const formatNumber = (
    value,
    digits = 2
  ) => {
    const n = Number(value);
    return Number.isFinite(n)
      ? n.toFixed(digits)
      : "—";
  };

  const formatDurationHours = (
    value
  ) => {
    const hours = Number(value);

    if (!Number.isFinite(hours)) {
      return "—";
    }

    if (hours < 24) {
      return `${hours.toFixed(1)} h`;
    }

    return `${(hours / 24).toFixed(
      1
    )} giorni`;
  };

  const tripCostStatusLabel = {
    pronto: "Calcolato",
    attesa_calibrazione:
      "Attesa calibrazione",
    attesa_prezzo_carburante:
      "Attesa prezzo carburante",
    maf_non_disponibile:
      "MAF non disponibile",
  };

  const calibrationLabel = {
    nessun_pieno: "In attesa del primo pieno",
    attesa_secondo_pieno:
      "Calibrazione in corso — attesa secondo pieno",
    preliminare:
      "Calibrazione preliminare",
    calibrata: "Calibrata",
    calibrata_robusta:
      "Calibrata robusta",
    dati_telemetrici_insufficienti:
      "Telemetria insufficiente",
  }[
    calibration?.status
  ] ||
    calibration?.status ||
    "—";

  return (
    <div
      className="flotta-rifornimenti-responsive"
      style={{
        display: "grid",
        gap: 16,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
      }}
    >
      <style>{`
        .flotta-rifornimenti-responsive,
        .flotta-rifornimenti-responsive * {
          box-sizing: border-box;
        }

        .flotta-rifornimenti-responsive {
          width: 100%;
          max-width: 100%;
          min-width: 0;
        }

        .flotta-rifornimenti-responsive > .flotta-card {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
        }

        .flotta-rifornimenti-responsive input,
        .flotta-rifornimenti-responsive textarea,
        .flotta-rifornimenti-responsive select,
        .flotta-rifornimenti-responsive button {
          max-width: 100%;
        }

        .flotta-rifornimenti-responsive p,
        .flotta-rifornimenti-responsive div,
        .flotta-rifornimenti-responsive strong,
        .flotta-rifornimenti-responsive span {
          overflow-wrap: anywhere;
        }

        .flotta-rifornimenti-responsive .flotta-responsive-table {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto !important;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
          scrollbar-width: thin;
        }

        .flotta-rifornimenti-responsive .flotta-responsive-table table {
          max-width: none;
        }

        @media (max-width: 900px) {
          .flotta-rifornimenti-responsive {
            gap: 10px !important;
          }

          .flotta-rifornimenti-responsive > .flotta-card {
            padding: 12px !important;
          }

          .flotta-rifornimenti-responsive h3 {
            font-size: 16px;
            line-height: 1.25;
          }

          .flotta-rifornimenti-responsive h4 {
            font-size: 14px;
          }

          .flotta-rifornimenti-responsive button {
            white-space: normal;
          }
        }

        @media (max-width: 720px) {
          .flotta-rifornimenti-responsive {
            gap: 8px !important;
          }

          .flotta-rifornimenti-responsive > .flotta-card {
            padding: 10px !important;
            border-radius: 10px;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table {
            overflow-x: visible !important;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table table,
          .flotta-rifornimenti-responsive .flotta-responsive-table thead,
          .flotta-rifornimenti-responsive .flotta-responsive-table tbody,
          .flotta-rifornimenti-responsive .flotta-responsive-table tr,
          .flotta-rifornimenti-responsive .flotta-responsive-table td {
            display: block;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table table {
            border-collapse: separate !important;
            border-spacing: 0;
            font-size: 12px !important;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table thead {
            display: none;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table tbody {
            display: grid;
            gap: 10px;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table tr {
            padding: 8px 10px;
            border: 1px solid #dde3ea;
            border-radius: 10px;
            background: #fff;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table td {
            display: grid;
            grid-template-columns: minmax(105px, 40%) minmax(0, 1fr);
            align-items: start;
            gap: 10px;
            padding: 6px 2px !important;
            border-bottom: 1px solid #edf0f4;
            text-align: right;
            white-space: normal;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table td:last-child {
            border-bottom: 0;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table td::before {
            font-weight: 700;
            opacity: 0.68;
            text-align: left;
          }

          .calibration-table td:nth-child(1)::before { content: "Intervallo"; }
          .calibration-table td:nth-child(2)::before { content: "Km"; }
          .calibration-table td:nth-child(3)::before { content: "Fonte km"; }
          .calibration-table td:nth-child(4)::before { content: "Litri reali"; }
          .calibration-table td:nth-child(5)::before { content: "Parziali"; }
          .calibration-table td:nth-child(6)::before { content: "L/100 km"; }
          .calibration-table td:nth-child(7)::before { content: "MAF aria"; }
          .calibration-table td:nth-child(8)::before { content: "g aria/L"; }
          .calibration-table td:nth-child(9)::before { content: "Qualità"; }

          .trip-cost-table td:nth-child(1)::before { content: "Viaggio"; }
          .trip-cost-table td:nth-child(2)::before { content: "Intervento"; }
          .trip-cost-table td:nth-child(3)::before { content: "Data"; }
          .trip-cost-table td:nth-child(4)::before { content: "Km"; }
          .trip-cost-table td:nth-child(5)::before { content: "MAF aria"; }
          .trip-cost-table td:nth-child(6)::before { content: "Copertura"; }
          .trip-cost-table td:nth-child(7)::before { content: "Litri stimati"; }
          .trip-cost-table td:nth-child(8)::before { content: "€/L"; }
          .trip-cost-table td:nth-child(9)::before { content: "Costo"; }
          .trip-cost-table td:nth-child(10)::before { content: "€/km"; }
          .trip-cost-table td:nth-child(11)::before { content: "Stato"; }

          .intervention-cost-table td:nth-child(1)::before { content: "Intervento"; }
          .intervention-cost-table td:nth-child(2)::before { content: "Viaggi"; }
          .intervention-cost-table td:nth-child(3)::before { content: "MAF"; }
          .intervention-cost-table td:nth-child(4)::before { content: "Con costo"; }
          .intervention-cost-table td:nth-child(5)::before { content: "Km"; }
          .intervention-cost-table td:nth-child(6)::before { content: "Litri stimati"; }
          .intervention-cost-table td:nth-child(7)::before { content: "Costo carburante"; }

          .refuel-history-table td:nth-child(1)::before { content: "Data"; }
          .refuel-history-table td:nth-child(2)::before { content: "Litri"; }
          .refuel-history-table td:nth-child(3)::before { content: "€/L"; }
          .refuel-history-table td:nth-child(4)::before { content: "Importo"; }
          .refuel-history-table td:nth-child(5)::before { content: "Pieno"; }
          .refuel-history-table td:nth-child(6)::before { content: "Odometro"; }
          .refuel-history-table td:nth-child(7)::before { content: "Fuel OBD"; }
          .refuel-history-table td:nth-child(8)::before { content: "Telemetria"; }
          .refuel-history-table td:nth-child(9)::before { content: "Note"; }
          .refuel-history-table td:nth-child(10)::before { content: "Azioni"; }

          .refuel-history-table td:nth-child(10) button {
            width: 100%;
          }

          .flotta-rifornimenti-responsive form > div {
            grid-template-columns: 1fr !important;
          }

          .flotta-rifornimenti-responsive textarea {
            min-height: 90px;
          }
        }

        @media (max-width: 420px) {
          .flotta-rifornimenti-responsive > .flotta-card {
            padding: 9px !important;
          }

          .flotta-rifornimenti-responsive .flotta-responsive-table td {
            grid-template-columns: minmax(90px, 42%) minmax(0, 1fr);
            gap: 8px;
          }

          .flotta-rifornimenti-responsive button {
            width: 100%;
          }
        }
      `}</style>
      <section className="flotta-card">
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3
              style={{
                marginTop: 0,
              }}
            >
              Rifornimenti assistiti —
              Fiorino
            </h3>
            <p
              style={{
                margin: "4px 0 0",
              }}
            >
              Telemetria, distanza Traccar,
              Fuel Level e MAF vengono
              agganciati automaticamente.
              L’odometro del cruscotto resta
              separato e opzionale. Litri reali
              e “pieno” vanno confermati
              sulla pompa.
            </p>
          </div>

          <button
            type="button"
            className="flotta-refresh-button"
            onClick={load}
            disabled={
              loading ||
              assistLoading
            }
          >
            {loading ||
            assistLoading
              ? "Aggiorno…"
              : "Aggiorna"}
          </button>
        </div>
      </section>

      {/* BETALUMINA_REFUEL_DASHBOARD_V1_20260926 */}
      <section className="flotta-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>
              Quadro rifornimenti
            </h3>
            <div style={{ fontSize: 12, opacity: 0.72 }}>
              Consumi reali, costi e calibrazione telemetrica del Fiorino.
            </div>
          </div>
          {/* BETALUMINA_REFUEL_DASHBOARD_V2_20260927 */}
          {/* BETALUMINA_REFUEL_DASHBOARD_V3_20260927 */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px 9px",
                borderRadius: 999,
                border: refuelDashboard.completed
                  ? "1px solid #b9dec4"
                  : "1px solid #ead59b",
                background: refuelDashboard.completed
                  ? "#eef8f1"
                  : "#fff8e6",
                color: refuelDashboard.completed
                  ? "#176b36"
                  : "#735300",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {refuelDashboard.completed
                ? "✓ Consumo reale disponibile"
                : "Consumo reale in attesa"}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px 9px",
                borderRadius: 999,
                border:
                  calibration?.status === "dati_telemetrici_insufficienti"
                    ? "1px solid #ead59b"
                    : "1px solid #cad9e8",
                background:
                  calibration?.status === "dati_telemetrici_insufficienti"
                    ? "#fff8e6"
                    : "#eef4fb",
                color:
                  calibration?.status === "dati_telemetrici_insufficienti"
                    ? "#735300"
                    : "#315d87",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {`MAF: ${calibrationLabel}`}
            </span>
          </div>
        </div>

        <div
          style={{
            marginTop: 12,
            padding: "11px 12px",
            borderRadius: 10,
            border: refuelDashboard.completed
              ? "1px solid #b9dec4"
              : "1px solid #ead59b",
            background: refuelDashboard.completed
              ? "#eef8f1"
              : "#fff8e6",
            lineHeight: 1.45,
            fontSize: 13,
          }}
        >
          <div>
            <strong>Consumo reale:</strong>{" "}
            {refuelDashboard.completed ? (
              <>
                disponibile da pieno a pieno
                {refuelDashboard.distanceKm !== null
                  ? ` · ${formatNumber(refuelDashboard.distanceKm, 1)} km`
                  : ""}
                {refuelDashboard.realLiters !== null
                  ? ` · ${formatNumber(refuelDashboard.realLiters, 2)} L reali`
                  : ""}
                {refuelDashboard.kmPerLiter !== null
                  ? ` · ${formatNumber(refuelDashboard.kmPerLiter, 2)} km/L`
                  : ""}
                {refuelDashboard.lPer100 !== null
                  ? ` · ${formatNumber(refuelDashboard.lPer100, 2)} L/100 km`
                  : ""}
              </>
            ) : (
              "in attesa del prossimo pieno completo."
            )}
          </div>
          <div style={{ marginTop: 4, opacity: 0.78 }}>
            <strong>MAF / telemetria:</strong>{" "}
            {calibrationLabel}. La calibrazione MAF è separata e non incide
            sul consumo reale pieno → pieno.
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
            gap: 10,
            marginTop: 12,
          }}
        >
          {[
            ["Ultimo rifornimento", refuelDashboard.latest ? formatDateTime(refuelDashboard.latest.rifornitoIl || refuelDashboard.latest.eventTimeUtc) : "—"],
            ["Litri ultimo", refuelDashboard.latest ? `${formatNumber(refuelDashboard.latest.litri, 2)} L` : "—"],
            ["Spesa ultimo", refuelDashboard.latest?.importoEuro != null ? euro(refuelDashboard.latest.importoEuro) : "—"],
            ["Tipo ultimo", refuelDashboard.latest ? (refuelDashboard.latest.pieno ? "Pieno completo" : "Rabbocco") : "—"],
            ["Km ultimo intervallo", refuelDashboard.distanceKm !== null ? `${formatNumber(refuelDashboard.distanceKm, 1)} km` : "—"],
            ["Litri consumati", refuelDashboard.realLiters !== null ? `${formatNumber(refuelDashboard.realLiters, 2)} L` : "—"],
            ["Consumo reale", refuelDashboard.kmPerLiter !== null ? `${formatNumber(refuelDashboard.kmPerLiter, 2)} km/L` : "—"],
            ["Consumo L/100 km", refuelDashboard.lPer100 !== null ? `${formatNumber(refuelDashboard.lPer100, 2)} L/100 km` : "—"],
            ["Costo stimato intervallo", refuelDashboard.estimatedIntervalCost !== null ? euro(refuelDashboard.estimatedIntervalCost) : "—"],
            ["Costo stimato €/km", refuelDashboard.euroPerKm !== null ? `${formatNumber(refuelDashboard.euroPerKm, 3)} €/km` : "—"],
            ["Prezzo medio storico", summary.weightedPrice !== null ? `${summary.weightedPrice.toFixed(3)} €/L` : "—"],
            ["Spesa totale", euro(summary.totalEuro)],
            ["Pieni registrati", summary.pieni],
            ["Rifornimenti totali", items.length],
          ].map(([label, value]) => (
            <div key={label} style={{ ...tileStyle, background: "#fff" }}>
              <div style={{ fontSize: 11, opacity: 0.68 }}>{label}</div>
              <strong style={{ display: "block", marginTop: 4, fontSize: 18 }}>{value}</strong>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 10,
            background: "#f5f7fa",
            lineHeight: 1.45,
          }}
        >
          {refuelDashboard.completed ? (
            <>
              <strong>Ultimo intervallo pieno → pieno:</strong>{" "}
              {refuelDashboard.distanceKm !== null
                ? `${formatNumber(refuelDashboard.distanceKm, 1)} km`
                : "distanza non disponibile"}
              {" · "}
              {refuelDashboard.realLiters !== null
                ? `${formatNumber(refuelDashboard.realLiters, 2)} L reali`
                : "litri non disponibili"}
              {" · "}
              {refuelDashboard.kmPerLiter !== null
                ? `${formatNumber(refuelDashboard.kmPerLiter, 2)} km/L`
                : "consumo non ancora calcolabile"}
              {" · fonte: "}
              {{
                traccar_totalDistance: "Traccar · distanza cumulativa",
                traccar_odometer: "Traccar · odometro",
                odometro: "Odometro manuale",
              }[refuelDashboard.completed.distanceSource] ||
                refuelDashboard.completed.distanceSource ||
                "non disponibile"}
            </>
          ) : (
            <>
              <strong>Consumo reale non ancora chiuso.</strong>{" "}
              {summary.pieni === 0
                ? "Registra il primo pieno completo per aprire l'intervallo."
                : "Il prossimo pieno completo chiuderà l'intervallo e produrrà km/L e L/100 km."}
            </>
          )}
        </div>

        {refuelDashboard.current && (
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
              gap: 10,
            }}
          >
            {[
              ["Km dall'ultimo pieno", refuelDashboard.currentDistance !== null ? `${formatNumber(refuelDashboard.currentDistance, 1)} km` : "—"],
              ["Tempo intervallo aperto", refuelDashboard.current.elapsedHours != null ? formatDurationHours(refuelDashboard.current.elapsedHours) : "—"],
              ["MAF aria accumulata", refuelDashboard.currentMaf !== null ? `${formatNumber(refuelDashboard.currentMaf, 0)} g` : "—"],
              ["Copertura MAF", refuelDashboard.currentCoverage !== null ? `${formatNumber(refuelDashboard.currentCoverage / 60, 1)} min` : "—"],
              ["Rabbocchi nell'intervallo", refuelDashboard.current.partialRefuelCount ?? 0],
              ["Fonte distanza", refuelDashboard.current.distance?.distanceSource || "—"],
            ].map(([label, value]) => (
              <div key={label} style={{ ...tileStyle, background: "#fff" }}>
                <div style={{ fontSize: 11, opacity: 0.68 }}>{label}</div>
                <strong style={{ display: "block", marginTop: 3 }}>{value}</strong>
              </div>
            ))}
          </div>
        )}

        {(refuelDashboard.mafEstimatedLiters !== null || refuelDashboard.mafDeltaPct !== null) && (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 8,
              background: "#f8fafc",
              fontSize: 12,
            }}
          >
            <strong>Controllo MAF ultimo intervallo:</strong>{" "}
            {refuelDashboard.mafEstimatedLiters !== null
              ? `${formatNumber(refuelDashboard.mafEstimatedLiters, 2)} L stimati`
              : "stima non disponibile"}
            {refuelDashboard.mafDeltaPct !== null
              ? ` · scostamento ${formatNumber(refuelDashboard.mafDeltaPct, 1)}% rispetto ai litri reali`
              : ""}
          </div>
        )}

        <div style={{ marginTop: 10, fontSize: 11, opacity: 0.7 }}>
          I costi dell'intervallo sono stimati usando il prezzo medio ponderato dei
          rifornimenti registrati; i consumi km/L e L/100 km derivano dai litri
          reali tra due pieni completi.
        </div>
      </section>

      <section className="flotta-card">
        <h3
          style={{
            marginTop: 0,
          }}
        >
          Telemetria assistita
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
          }}
        >
          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Fix Traccar
            </div>
            <strong>
              {assist?.traccar
                ?.usableForTarget
                ? "Agganciato"
                : "Non vicino all'ora"}
            </strong>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
              }}
            >
              {formatDateTime(
                assist?.traccar
                  ?.positionTimestamp
              )}
            </div>
          </div>

          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Distanza telematica cumulativa
            </div>
            <strong>
              {assist?.traccar
                ?.trackerTotalDistanceKm ===
                null ||
              assist?.traccar
                ?.trackerTotalDistanceKm ===
                undefined
                ? "—"
                : `${Number(
                    assist.traccar
                      .trackerTotalDistanceKm
                  ).toFixed(1)} km`}
            </strong>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                lineHeight: 1.35,
              }}
            >
              Contatore Traccar: vale il
              <strong> delta tra due pieni</strong>,
              non sono i km del cruscotto.
              {assist?.traccar
                ?.trackerOdometerKm !== null &&
              assist?.traccar
                ?.trackerOdometerKm !==
                undefined
                ? ` · odometer tracker ${Number(
                    assist.traccar
                      .trackerOdometerKm
                  ).toFixed(1)} km`
                : ""}
            </div>
          </div>

          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Fuel OBD vicino
            </div>
            <strong>
              {assist?.obd?.nearest
                ?.fuelLevelFilteredPct ===
                null ||
              assist?.obd?.nearest
                ?.fuelLevelFilteredPct ===
                undefined
                ? "—"
                : `${Number(
                    assist.obd.nearest
                      .fuelLevelFilteredPct
                  ).toFixed(1)} %`}
            </strong>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
              }}
            >
              {assist?.obd?.nearest
                ? `Δt ${assist.obd.nearest.deltaSeconds}s`
                : "nessun campione ±30 min"}
            </div>
          </div>

          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              MAF vicino
            </div>
            <strong>
              {assist?.obd?.nearest
                ?.mafGps === null ||
              assist?.obd?.nearest
                ?.mafGps ===
                undefined
                ? "—"
                : `${Number(
                    assist.obd.nearest
                      .mafGps
                  ).toFixed(2)} g/s`}
            </strong>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
              }}
            >
              {assist?.obd?.nearest
                ?.engineRpm === null ||
              assist?.obd?.nearest
                ?.engineRpm ===
                undefined
                ? "—"
                : `${Number(
                    assist.obd.nearest
                      .engineRpm
                  ).toFixed(0)} rpm`}
            </div>
          </div>
        </div>

        {candidate && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              background: "#fff8df",
              border:
                "1px solid #ead89c",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent:
                  "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div>
                <strong>
                  Possibile rifornimento
                  rilevato
                </strong>{" "}
                <StatusPill>
                  confidenza{" "}
                  {candidate.confidence}
                </StatusPill>
                <div
                  style={{
                    marginTop: 5,
                  }}
                >
                  Fuel Level{" "}
                  {candidate.fuelBeforePct}%
                  {" → "}
                  {candidate.fuelAfterPct}%
                  {" (+"}
                  {candidate.deltaPct}
                  {" punti) · "}
                  {formatDateTime(
                    candidate.timestamp
                  )}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                  }}
                >
                  È solo un candidato:
                  non vengono inventati
                  litri né “pieno”.
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  applyCandidate(
                    candidate
                  )
                }
                style={{
                  ...buttonStyle,
                  background: "#e7b83b",
                }}
              >
                Usa candidato
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="flotta-card">
        <h3
          style={{
            marginTop: 0,
          }}
        >
          {editingId
            ? `Modifica rifornimento #${editingId}`
            : "Nuovo rifornimento"}
        </h3>

        <form onSubmit={save}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(165px, 1fr))",
              gap: 12,
            }}
          >
            <label style={labelStyle}>
              Data e ora *
              <input
                type="datetime-local"
                value={
                  form.rifornitoIl
                }
                onChange={(e) =>
                  updateField(
                    "rifornitoIl",
                    e.target.value
                  )
                }
                required
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Litri reali *
              <input
                type="number"
                min="0.001"
                step="0.001"
                value={form.litri}
                onChange={(e) =>
                  onLitriOrPrezzo(
                    "litri",
                    e.target.value
                  )
                }
                required
                inputMode="decimal"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Prezzo €/L
              <input
                type="number"
                min="0.001"
                step="0.001"
                value={
                  form.prezzoEuroLitro
                }
                onChange={(e) =>
                  onLitriOrPrezzo(
                    "prezzoEuroLitro",
                    e.target.value
                  )
                }
                inputMode="decimal"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Importo €
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={
                  form.importoEuro
                }
                onChange={(e) =>
                  updateField(
                    "importoEuro",
                    e.target.value
                  )
                }
                inputMode="decimal"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Odometro cruscotto km (opzionale)
              <input
                type="number"
                min="0"
                step="0.1"
                value={
                  form.odometroKm
                }
                onChange={(e) =>
                  updateOdometer(
                    e.target.value
                  )
                }
                inputMode="decimal"
                style={inputStyle}
              />
              <span
                style={{
                  fontSize: 11,
                  opacity: 0.68,
                }}
              >
                Inserisci solo i km letti dal
                quadro strumenti. Traccar salva
                separatamente i propri contatori
                e calcola automaticamente il delta.
              </span>
            </label>
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              marginTop: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.pieno}
              onChange={(e) =>
                updateField(
                  "pieno",
                  e.target.checked
                )
              }
            />
            Pieno reale — riferimento
            per calibrazione
            pieno→pieno
          </label>

          <label
            style={{
              ...labelStyle,
              marginTop: 14,
            }}
          >
            Note
            <textarea
              value={form.note}
              maxLength={2000}
              rows={3}
              onChange={(e) =>
                updateField(
                  "note",
                  e.target.value
                )
              }
              style={{
                ...inputStyle,
                resize: "vertical",
              }}
            />
          </label>

          {error && (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 8,
                background: "#fff0f0",
              }}
            >
              {error}
            </div>
          )}

          {message && (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 8,
                background: "#f0fff4",
              }}
            >
              {message}
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 14,
              flexWrap: "wrap",
            }}
          >
            <button
              type="submit"
              disabled={saving}
              style={{
                ...buttonStyle,
                background: "#1f6feb",
                color: "#fff",
                opacity: saving
                  ? 0.65
                  : 1,
              }}
            >
              {saving
                ? "Salvataggio…"
                : editingId
                ? "Salva modifiche"
                : "Registra rifornimento"}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                style={{
                  ...buttonStyle,
                  background: "#e9edf2",
                }}
              >
                Annulla modifica
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="flotta-card">
        <h3
          style={{
            marginTop: 0,
          }}
        >
          Consumi & calibrazione MAF
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 10,
          }}
        >
          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Stato
            </div>
            <strong>
              {calibrationLabel}
            </strong>
          </div>

          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Pieni reali
            </div>
            <strong>
              {calibration
                ?.fullRefuels ?? 0}
            </strong>
          </div>

          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Intervalli validi
            </div>
            <strong>
              {calibration
                ?.validIntervals ?? 0}
            </strong>
          </div>

          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Coefficiente empirico
            </div>
            <strong>
              {calibration?.profile
                ?.empiricalAirGramsPerLiter
                ? `${Number(
                    calibration.profile
                      .empiricalAirGramsPerLiter
                  ).toFixed(0)} g aria/L`
                : "—"}
            </strong>
          </div>

          <div style={tileStyle}>
            <div
              style={{
                fontSize: 12,
                opacity: 0.72,
              }}
            >
              Confidenza
            </div>
            <strong>
              {calibration?.profile
                ?.confidence ||
                "—"}
            </strong>
          </div>
        </div>

        {calibration?.currentInterval && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 10,
              background: "#eef6ff",
              border:
                "1px solid #bfd8f2",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "flex-start",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 10,
              }}
            >
              <div>
                <strong
                  style={{
                    fontSize: 15,
                  }}
                >
                  Intervallo di calibrazione
                  in corso
                </strong>
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 12,
                    opacity: 0.75,
                  }}
                >
                  Dal pieno #
                  {
                    calibration
                      .currentInterval
                      .startRefuelId
                  }
                  {" · "}
                  {formatDateTime(
                    calibration
                      .currentInterval
                      .startTimeUtc ||
                      calibration
                        .currentInterval
                        .startLocal
                  )}
                </div>
              </div>

              <StatusPill>
                raccolta attiva
              </StatusPill>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 8,
              }}
            >
              {[
                [
                  "Km dal pieno",
                  calibration
                    .currentInterval
                    .distance
                    ?.distanceKm ==
                  null
                    ? "—"
                    : `${formatNumber(
                        calibration
                          .currentInterval
                          .distance
                          .distanceKm,
                        2
                      )} km`,
                ],
                [
                  "Tempo trascorso",
                  formatDurationHours(
                    calibration
                      .currentInterval
                      .elapsedHours
                  ),
                ],
                [
                  "Viaggi completati",
                  calibration
                    .currentInterval
                    .trips
                    ?.completedTrips ??
                    0,
                ],
                [
                  "Viaggi con MAF",
                  `${calibration
                    .currentInterval
                    .trips
                    ?.capturedMafTrips ??
                    0}/${calibration
                    .currentInterval
                    .trips
                    ?.completedTrips ??
                    0}`,
                ],
                [
                  "MAF RAW accumulato",
                  calibration
                    .currentInterval
                    .rawMaf
                    ?.airMassGrams ==
                  null
                    ? "—"
                    : `${formatNumber(
                        calibration
                          .currentInterval
                          .rawMaf
                          .airMassGrams,
                        0
                      )} g`,
                ],
                [
                  "Copertura MAF valida",
                  calibration
                    .currentInterval
                    .rawMaf
                    ?.coverageSeconds
                    ? `${formatNumber(
                        calibration
                          .currentInterval
                          .rawMaf
                          .coverageSeconds /
                          60,
                        1
                      )} min`
                    : "—",
                ],
                [
                  "Campioni RAW",
                  calibration
                    .currentInterval
                    .rawMaf
                    ?.sampleCount ??
                    0,
                ],
                [
                  "Gap esclusi",
                  calibration
                    .currentInterval
                    .rawMaf
                    ?.gapsDiscarded ??
                    0,
                ],
                [
                  "Rabbocchi dopo pieno",
                  calibration
                    .currentInterval
                    .partialRefuelCount ??
                    0,
                ],
              ].map(
                ([label, value]) => (
                  <div
                    key={label}
                    style={{
                      ...tileStyle,
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        opacity: 0.68,
                      }}
                    >
                      {label}
                    </div>
                    <strong
                      style={{
                        display: "block",
                        marginTop: 3,
                      }}
                    >
                      {value}
                    </strong>
                  </div>
                )
              )}
            </div>

            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              <strong>
                La strada fatta dopo il
                primo pieno non è persa.
              </strong>{" "}
              È già compresa
              nell’intervallo aperto.
              Il secondo pieno reale
              serve solo a conoscere i
              litri effettivamente
              consumati e quindi chiudere
              il calcolo di L/100 km e
              del coefficiente MAF.
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                opacity: 0.7,
              }}
            >
              Fonte distanza:{" "}
              {calibration
                .currentInterval
                .distance
                ?.distanceSource ||
                "non disponibile"}
              {" · "}
              Stato RAW:{" "}
              {calibration
                .currentInterval
                .rawMaf?.status ||
                "non disponibile"}
            </div>
          </div>
        )}

        <p
          style={{
            marginBottom: 0,
            fontSize: 12,
            opacity: 0.75,
          }}
        >
          La distanza pieno→pieno usa
          l’odometro del cruscotto se lo
          inserisci su entrambi i pieni;
          altrimenti usa automaticamente il
          delta Traccar totalDistance
          (fallback: delta Traccar odometer).
          I litri includono anche eventuali
          rifornimenti parziali tra i due pieni.
          Il coefficiente MAF è empirico, non
          usa un AFR diesel teorico.
        </p>

        {Array.isArray(
          calibration?.intervals
        ) &&
          calibration.intervals
            .length > 0 && (
            <div
              className="flotta-responsive-table calibration-table"
              style={{
                overflowX: "auto",
                marginTop: 12,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse:
                    "collapse",
                  minWidth: 760,
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    {[
                      "Intervallo",
                      "Km",
                      "Fonte km",
                      "Litri reali",
                      "Parziali",
                      "L/100 km",
                      "MAF aria",
                      "g aria/L",
                      "Qualità",
                    ].map((label) => (
                      <th
                        key={label}
                        style={{
                          textAlign:
                            "left",
                          padding:
                            "9px 8px",
                          borderBottom:
                            "1px solid #d8dee6",
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {calibration.intervals.map(
                    (interval) => (
                      <tr
                        key={`${interval.startRefuelId}-${interval.endRefuelId}`}
                      >
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          #
                          {
                            interval.startRefuelId
                          }
                          {" → #"}
                          {
                            interval.endRefuelId
                          }
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {interval.distanceKm ??
                            "—"}
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {interval.distanceSource ||
                            "—"}
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {Number(
                            interval.realLiters
                          ).toFixed(3)}
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {interval.partialRefuelCount ??
                            0}
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {interval.realLPer100Km ??
                            "—"}
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {interval.maf
                            ?.airMassGrams
                            ? `${Number(
                                interval
                                  .maf
                                  .airMassGrams
                              ).toFixed(
                                0
                              )} g`
                            : "—"}
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {interval.airGramsPerLiter ??
                            "—"}
                        </td>
                        <td
                          style={{
                            padding:
                              "9px 8px",
                          }}
                        >
                          {interval.calibrationUsable
                            ? "OK"
                            : "Incompleto"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
      </section>

      <section className="flotta-card">
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3
              style={{
                marginTop: 0,
                marginBottom: 4,
              }}
            >
              Costi viaggi & interventi
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 12,
                opacity: 0.75,
              }}
            >
              Il MAF di ogni viaggio completato
              viene conservato automaticamente.
              Litri e costi compaiono solo quando
              esistono calibrazione e prezzo reale
              del carburante.
            </p>
          </div>

          <button
            type="button"
            onClick={syncTripCosts}
            disabled={
              tripCostsSyncing ||
              tripCostsLoading
            }
            style={{
              ...buttonStyle,
              background: "#e9edf2",
            }}
          >
            {tripCostsSyncing
              ? "Sincronizzo…"
              : "Sincronizza MAF ultimi 7 giorni"}
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
            marginTop: 12,
          }}
        >
          {[
            [
              "Viaggi completati",
              tripCosts?.summary
                ?.completedTrips ?? "—",
            ],
            [
              "MAF catturato",
              tripCosts?.summary
                ?.capturedMafTrips ?? "—",
            ],
            [
              "Viaggi con costo",
              tripCosts?.summary
                ?.costedTrips ?? "—",
            ],
            [
              "Litri stimati",
              tripCosts?.summary
                ?.totalEstimatedLiters ===
              null ||
              tripCosts?.summary
                ?.totalEstimatedLiters ===
                undefined
                ? "—"
                : `${formatNumber(
                    tripCosts.summary
                      .totalEstimatedLiters,
                    3
                  )} L`,
            ],
            [
              "Costo carburante",
              tripCosts?.summary
                ?.totalFuelCostEuro ===
              null ||
              tripCosts?.summary
                ?.totalFuelCostEuro ===
                undefined
                ? "—"
                : euro(
                    tripCosts.summary
                      .totalFuelCostEuro
                  ),
            ],
          ].map(([label, value]) => (
            <div
              key={label}
              style={tileStyle}
            >
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.72,
                }}
              >
                {label}
              </div>
              <strong
                style={{
                  display: "block",
                  marginTop: 4,
                  fontSize: 17,
                }}
              >
                {value}
              </strong>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 8,
            background: "#f5f7fa",
            fontSize: 12,
          }}
        >
          Stato calibrazione:{" "}
          <strong>
            {tripCosts?.calibration
              ?.status ||
              "non disponibile"}
          </strong>
          . I km vengono sempre letti dal viaggio
          reale (GPS, con fallback ai contatori
          Traccar) anche quando il MAF non è
          disponibile. Prima della calibrazione i
          grammi d'aria MAF vengono comunque
          archiviati: quando arriverà il coefficiente
          del Fiorino, litri e costi potranno essere
          ricalcolati senza perdere lo storico.
        </div>

        {tripCostsLoading ? (
          <p>Caricamento costi viaggi…</p>
        ) : Array.isArray(
            tripCosts?.trips
          ) &&
          tripCosts.trips.length > 0 ? (
          <div
            className="flotta-responsive-table trip-cost-table"
            style={{
              overflowX: "auto",
              marginTop: 12,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse:
                  "collapse",
                minWidth: 1100,
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  {[
                    "Viaggio",
                    "Intervento",
                    "Data",
                    "Km",
                    "MAF aria",
                    "Copertura",
                    "Litri stimati",
                    "€/L",
                    "Costo",
                    "€/km",
                    "Stato",
                  ].map((label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: "left",
                        padding: "8px 7px",
                        borderBottom:
                          "1px solid #d8dee6",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {(showAllTripCosts
                  ? tripCosts.trips
                  : tripCosts.trips.slice(0, 10)
                ).map(
                  (tripCost) => (
                    <tr
                      key={
                        tripCost.tripId
                      }
                    >
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        #{tripCost.tripId}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.interventionNumber ||
                          (tripCost.interventionId
                            ? `#${tripCost.interventionId}`
                            : "—")}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {formatDateTime(
                          tripCost.startTime
                        )}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.distanceKm ===
                          null
                          ? "—"
                          : formatNumber(
                              tripCost.distanceKm,
                              2
                            )}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.mafAirMassGrams ===
                          null
                          ? "—"
                          : `${formatNumber(
                              tripCost.mafAirMassGrams,
                              0
                            )} g`}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.mafCoveragePct ===
                          null
                          ? "—"
                          : `${formatNumber(
                              tripCost.mafCoveragePct,
                              0
                            )}%`}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.estimatedLiters ===
                          null
                          ? "—"
                          : `${formatNumber(
                              tripCost.estimatedLiters,
                              3
                            )} L`}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.priceEuroLiter ===
                          null
                          ? "—"
                          : `${formatNumber(
                              tripCost.priceEuroLiter,
                              3
                            )} €`}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.fuelCostEuro ===
                          null
                          ? "—"
                          : euro(
                              tripCost.fuelCostEuro
                            )}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCost.euroPerKm ===
                          null
                          ? "—"
                          : `${formatNumber(
                              tripCost.euroPerKm,
                              3
                            )} €`}
                      </td>
                      <td
                        style={{
                          padding:
                            "8px 7px",
                        }}
                      >
                        {tripCostStatusLabel[
                          tripCost.estimateStatus
                        ] ||
                          tripCost.estimateStatus ||
                          "—"}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>

            {tripCosts.trips.length > 10 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 10,
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setShowAllTripCosts(
                      (current) => !current
                    )
                  }
                  style={{
                    ...buttonStyle,
                    background: "#e9edf2",
                    padding: "8px 12px",
                  }}
                >
                  {showAllTripCosts
                    ? "Mostra solo gli ultimi 10"
                    : `Mostra tutti i ${tripCosts.trips.length} viaggi`}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p
            style={{
              marginBottom: 0,
            }}
          >
            Nessun viaggio completato
            disponibile.
          </p>
        )}

        {Array.isArray(
          tripCosts?.interventions
        ) &&
          tripCosts.interventions
            .length > 0 && (
            <>
              <h4
                style={{
                  marginBottom: 6,
                  marginTop: 18,
                }}
              >
                Totali per intervento
              </h4>

              <div
                className="flotta-responsive-table intervention-cost-table"
                style={{
                  overflowX: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse:
                      "collapse",
                    minWidth: 760,
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr>
                      {[
                        "Intervento",
                        "Viaggi",
                        "MAF",
                        "Con costo",
                        "Km",
                        "Litri stimati",
                        "Costo carburante",
                      ].map((label) => (
                        <th
                          key={label}
                          style={{
                            textAlign:
                              "left",
                            padding:
                              "8px 7px",
                            borderBottom:
                              "1px solid #d8dee6",
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {tripCosts.interventions.map(
                      (item) => (
                        <tr
                          key={
                            item.interventionId
                          }
                        >
                          <td
                            style={{
                              padding:
                                "8px 7px",
                            }}
                          >
                            {item.interventionNumber ||
                              `#${item.interventionId}`}
                          </td>
                          <td
                            style={{
                              padding:
                                "8px 7px",
                            }}
                          >
                            {
                              item.completedTrips
                            }
                          </td>
                          <td
                            style={{
                              padding:
                                "8px 7px",
                            }}
                          >
                            {
                              item.capturedMafTrips
                            }
                          </td>
                          <td
                            style={{
                              padding:
                                "8px 7px",
                            }}
                          >
                            {
                              item.costedTrips
                            }
                          </td>
                          <td
                            style={{
                              padding:
                                "8px 7px",
                            }}
                          >
                            {formatNumber(
                              item.totalDistanceKm,
                              2
                            )}
                          </td>
                          <td
                            style={{
                              padding:
                                "8px 7px",
                            }}
                          >
                            {item.estimatedLiters ===
                              null
                              ? "—"
                              : `${formatNumber(
                                  item.estimatedLiters,
                                  3
                                )} L`}
                          </td>
                          <td
                            style={{
                              padding:
                                "8px 7px",
                            }}
                          >
                            {item.fuelCostEuro ===
                              null
                              ? "—"
                              : euro(
                                  item.fuelCostEuro
                                )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </section>

      <section className="flotta-card">
        <h3
          style={{
            marginTop: 0,
          }}
        >
          Riepilogo storico
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(145px, 1fr))",
            gap: 10,
          }}
        >
          {[
            ["Rifornimenti", items.length],
            ["Pieni", summary.pieni],
            [
              "Litri registrati",
              `${summary.totalLitri.toFixed(
                2
              )} L`,
            ],
            [
              "Spesa registrata",
              euro(
                summary.totalEuro
              ),
            ],
            [
              "Prezzo medio pesato",
              summary.weightedPrice ===
              null
                ? "—"
                : `${summary.weightedPrice.toFixed(
                    3
                  )} €/L`,
            ],
          ].map(
            ([label, value]) => (
              <div
                key={label}
                style={tileStyle}
              >
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.72,
                  }}
                >
                  {label}
                </div>
                <strong
                  style={{
                    display: "block",
                    marginTop: 4,
                    fontSize: 18,
                  }}
                >
                  {value}
                </strong>
              </div>
            )
          )}
        </div>
      </section>

      <section className="flotta-card">
        <h3
          style={{
            marginTop: 0,
          }}
        >
          Storico rifornimenti
        </h3>

        {loading ? (
          <p>Caricamento…</p>
        ) : items.length === 0 ? (
          <p>
            Nessun rifornimento
            registrato. Gli automatismi
            sono pronti ma non
            inseriscono dati reali da
            soli.
          </p>
        ) : (
          <div
            className="flotta-responsive-table refuel-history-table"
            style={{
              overflowX: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse:
                  "collapse",
                minWidth: 1050,
                fontSize: 13,
              }}
            >
              <thead>
                <tr>
                  {[
                    "Data",
                    "Litri",
                    "€/L",
                    "Importo",
                    "Pieno",
                    "Odometro",
                    "Fuel OBD",
                    "Telemetria",
                    "Note",
                    "",
                  ].map((label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: "left",
                        padding: "9px 8px",
                        borderBottom:
                          "1px solid #d8dee6",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {formatDateTime(
                        item.rifornitoIl
                      )}
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {Number(
                        item.litri
                      ).toFixed(3)}{" "}
                      L
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {item.prezzoEuroLitro ===
                      null
                        ? "—"
                        : `${Number(
                            item.prezzoEuroLitro
                          ).toFixed(
                            3
                          )} €`}
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {euro(
                        item.importoEuro
                      )}
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {item.pieno
                        ? "Sì"
                        : "No"}
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {item.odometroKm ===
                      null
                        ? "—"
                        : `${Number(
                            item.odometroKm
                          ).toFixed(
                            1
                          )} km`}
                      <div
                        style={{
                          fontSize: 11,
                          opacity: 0.65,
                        }}
                      >
                        {item.odometroSource ===
                        "manuale_cruscotto"
                          ? "cruscotto"
                          : item.odometroSource ||
                            "—"}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {item.telemetry
                        ?.fuelLevelFilteredPct ===
                      null
                        ? "—"
                        : `${Number(
                            item.telemetry
                              .fuelLevelFilteredPct
                          ).toFixed(
                            1
                          )}%`}
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      {item.telemetry
                        ?.timestamp
                        ? formatDateTime(
                            item.telemetry
                              .timestamp
                          )
                        : "—"}
                      <div
                        style={{
                          fontSize: 11,
                          opacity: 0.65,
                        }}
                      >
                        {item.telemetry
                          ?.matchQuality ||
                          item.source ||
                          "—"}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                        maxWidth: 240,
                      }}
                    >
                      {item.note || "—"}
                    </td>

                    <td
                      style={{
                        padding: "9px 8px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          editItem(item)
                        }
                        style={{
                          ...buttonStyle,
                          padding:
                            "7px 10px",
                          background:
                            "#e9edf2",
                        }}
                      >
                        Modifica
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default FlottaRifornimenti;
